"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { Link } from "@/i18n/navigation";
import { Clapperboard, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";
import { onboardingArtFor } from "@/lib/brand";


export interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  source?: "pre_auth" | "post_auth";
}

const INTRO_SLIDES = 3;

const VIDEO_SLIDES = [
  { video: "onb-video-carousel.mp4", image: "onb-carousel-preview.webp", titleKey: "videoCarouselTitle", descKey: "videoCarouselDesc", btnKey: "videoCarouselBtn" },
  { video: "onb-video-reels.mp4", image: "onb-reels-preview.webp", titleKey: "videoReelsTitle", descKey: "videoReelsDesc", btnKey: "videoReelsBtn" },
  { video: "onb-video-stories.mp4", image: "onb-stories-preview.png", titleKey: "videoStoriesTitle", descKey: "videoStoriesDesc" },
  { video: "onb-video-telegram.mp4", image: "onb-telegram-preview.webp", titleKey: "videoSocialPostTitle", descKey: "videoSocialPostDesc", btnKey: "videoSocialPostBtn" },
  { video: "onb-video-threads.mp4", image: "onb-threads-preview.png", titleKey: "videoThreadsTitle", descKey: "videoThreadsDesc", btnKey: "videoThreadsBtn", footnoteKey: "videoThreadsFootnote" },
] as const;

const TOTAL_SLIDES = INTRO_SLIDES + VIDEO_SLIDES.length;

type GalleryCard = { src: string; ratio: "square" | "portrait" | "story" };

const GALLERY_COLS: GalleryCard[][] = [
  [
    { src: "gallery/g5.webp", ratio: "story" },
    { src: "gallery/g30.webp", ratio: "portrait" },
    { src: "gallery/g37.webp", ratio: "square" },
  ],
  [
    { src: "gallery/g32.webp", ratio: "story" },
    { src: "gallery/g31.webp", ratio: "portrait" },
    { src: "gallery/g35.webp", ratio: "square" },
  ],
  [
    { src: "gallery/g36.webp", ratio: "story" },
    { src: "gallery/g33.webp", ratio: "square" },
    { src: "gallery/g34.webp", ratio: "story" },
  ],
];

const RATIO_CLASS = {
  square: "aspect-square",
  portrait: "aspect-[4/5]",
  story: "aspect-[9/16]",
} as const;

const SLIDE_NAMES = [
  "welcome_gallery",
  "one_text_five_formats",
  "voice_text_photos",
  ...VIDEO_SLIDES.map((s) => s.video.replace("onb-video-", "").replace(".mp4", "")),
] as const;

const MOCK_PHOTOS = ["mock-photo-1.webp", "mock-photo-2.webp", "mock-photo-3.webp", "mock-photo-4.webp", "mock-photo-5.webp", "mock-photo-6.webp"];

export function OnboardingModal({ isOpen, onComplete, source = "post_auth" }: OnboardingModalProps) {
  // Картинки тура — скриншоты интерфейса, поэтому набор выбирает локаль
  // страницы, а не бренд: mint24.app/ru показывает русские экраны,
  // mint24.app/en — английские, getmintai.app всегда английские.
  const { brand } = useSiteFlavor();
  const locale = useLocale();
  const art = onboardingArtFor(brand, locale);
  const t = useTranslations("onboarding");
  const posthog = usePostHog();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesRef = useRef<HTMLDivElement>(null);
  const currentSlideRef = useRef(currentSlide);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Analytics refs
  const slideEnteredAt = useRef(Date.now());
  const onboardingStartedAt = useRef(Date.now());
  const navigationMethod = useRef<string>("auto");
  const prevSlideRef = useRef<number | null>(null);

  currentSlideRef.current = currentSlide;

  const [isMuted, setIsMuted] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [btnShake, setBtnShake] = useState(false);

  const isLastSlide = currentSlide === TOTAL_SLIDES - 1;
  const isVideoSlide = currentSlide >= INTRO_SLIDES;
  const videoSlideIndex = currentSlide - INTRO_SLIDES;

  const handleVideoEnded = useCallback((idx: number) => {
    const video = videoRefs.current[idx];
    if (!video) return;
    // Bump key to force animation re-mount, then replay video
    setBtnShake(true);
    setShakeKey((k) => k + 1);
    setTimeout(() => setBtnShake(false), 600);
    setTimeout(() => {
      video.currentTime = 0;
      video.play().catch(() => {});
    }, 800);
  }, []);

  // Track slide views
  useEffect(() => {
    if (!isOpen) return;
    const now = Date.now();
    const prevDuration =
      prevSlideRef.current !== null
        ? Math.round((now - slideEnteredAt.current) / 1000)
        : null;

    posthog?.capture("onboarding_slide_viewed", {
      slide: currentSlide + 1,
      slide_name: SLIDE_NAMES[currentSlide],
      method: navigationMethod.current,
      prev_slide_duration_sec: prevDuration,
      source,
    });

    prevSlideRef.current = currentSlide;
    slideEnteredAt.current = now;
    navigationMethod.current = "swipe"; // reset default
  }, [currentSlide, isOpen, posthog, source]);

  // Play/pause videos + preload nearby ones
  useEffect(() => {
    setBtnShake(false);
    const currentVideoIdx = currentSlide - INTRO_SLIDES;
    videoRefs.current.forEach((video, idx) => {
      if (!video) return;
      video.muted = isMuted;
      if (idx === currentVideoIdx) {
        // Current video: ensure loaded and play
        if (video.preload === "none") {
          video.preload = "auto";
          video.load();
        }
        video.currentTime = 0;
        video.play().catch(() => {});
      } else if (Math.abs(idx - currentVideoIdx) <= 1) {
        // Adjacent video: preload but don't play
        video.pause();
        if (video.preload === "none") {
          video.preload = "auto";
          video.load();
        }
      } else {
        video.pause();
      }
    });
  }, [currentSlide, isMuted]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      posthog?.capture("onboarding_mute_toggled", { muted: !prev });
      return !prev;
    });
  }, [posthog]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const slideWidth = container.offsetWidth;
    if (slideWidth === 0) return;
    const newSlide = Math.round(scrollLeft / slideWidth);
    if (
      newSlide !== currentSlideRef.current &&
      newSlide >= 0 &&
      newSlide < TOTAL_SLIDES
    ) {
      currentSlideRef.current = newSlide;
      setCurrentSlide(newSlide);
    }
  }, []);

  const scrollToSlide = useCallback((index: number) => {
    if (!slidesRef.current) return;
    const slideWidth = slidesRef.current.offsetWidth;
    slidesRef.current.scrollTo({ left: slideWidth * index, behavior: "smooth" });
  }, []);

  const handleNext = useCallback(() => {
    if (isLastSlide) {
      posthog?.capture("onboarding_completed", {
        total_duration_sec: Math.round(
          (Date.now() - onboardingStartedAt.current) / 1000
        ),
        source,
      });
      onComplete();
    } else {
      navigationMethod.current = "button";
      scrollToSlide(currentSlide + 1);
    }
  }, [isLastSlide, currentSlide, scrollToSlide, onComplete, posthog, source]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      navigationMethod.current = "button";
      scrollToSlide(currentSlide - 1);
    }
  }, [currentSlide, scrollToSlide]);

  const handleDotClick = useCallback(
    (index: number) => {
      navigationMethod.current = "dot";
      scrollToSlide(index);
    },
    [scrollToSlide]
  );

  // Keyboard navigation (desktop)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        navigationMethod.current = "keyboard";
        if (currentSlideRef.current < TOTAL_SLIDES - 1) {
          scrollToSlide(currentSlideRef.current + 1);
        }
      } else if (e.key === "ArrowLeft" && currentSlideRef.current > 0) {
        e.preventDefault();
        navigationMethod.current = "keyboard";
        scrollToSlide(currentSlideRef.current - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        navigationMethod.current = "keyboard";
        handleNext();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, handleNext, scrollToSlide]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-surface animate-fade-in">
      {/* Hero image always at root level — covered by bg-surface on other slides */}
      {/* Dark theme is default; light theme uses [data-theme="light"] → @custom-variant light */}
      {/* Mobile */}
      <div className="lg:hidden absolute inset-0 z-0 pointer-events-none">
        <img src={`${art}/hero-woman-dark-mobile.webp`} alt="" draggable={false}
          className="light:hidden w-full h-full object-contain object-top" />
        <img src={`${art}/hero-woman.webp`} alt="" draggable={false}
          className="hidden light:block w-full h-full object-contain object-top" />
      </div>
      {/* Desktop */}
      <div className="hidden lg:block absolute inset-0 z-0 pointer-events-none">
        <img src={`${art}/hero-woman-desktop-dark.webp`} alt="" draggable={false}
          className="light:hidden w-full h-full object-contain object-top" />
        <img src={`${art}/hero-woman-desktop.webp`} alt="" draggable={false}
          className="hidden light:block w-full h-full object-contain object-top" />
      </div>
      <div className="relative z-[2] flex flex-1 flex-col min-h-0 max-w-5xl mx-auto w-full">
        {/* Progress bar (stories-style) — all slides */}
        <div className="absolute top-0 left-0 right-0 z-10 px-4 lg:px-8 pt-[calc(8px+env(safe-area-inset-top))] lg:pt-4 flex gap-1.5">
          {Array.from({ length: TOTAL_SLIDES }, (_, idx) => (
            <div
              key={idx}
              className="h-[3px] flex-1 rounded-full transition-colors duration-300"
              style={{
                backgroundColor: idx <= currentSlide ? "var(--ink)" : "var(--line-strong)",
              }}
            />
          ))}
        </div>

        {/* Sound toggle — hidden on mobile, video slides only */}
        {isVideoSlide && (
          <button
            className="hidden lg:flex absolute top-0 right-3 lg:right-6 z-10 mt-[calc(24px+env(safe-area-inset-top))] lg:mt-6 w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-surface-mute/80 backdrop-blur-sm items-center justify-center cursor-pointer active:scale-90 transition-transform"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-ink">
                <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-ink">
                <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
          </button>
        )}

        {/* Desktop navigation arrows */}
        {currentSlide > 0 && (
          <button
            className="hidden lg:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-surface-mute/80 backdrop-blur-sm items-center justify-center cursor-pointer hover:bg-surface-mute transition-colors"
            onClick={handlePrev}
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} className="text-ink" />
          </button>
        )}
        {!isLastSlide && (
          <button
            className="hidden lg:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-surface-mute/80 backdrop-blur-sm items-center justify-center cursor-pointer hover:bg-surface-mute transition-colors"
            onClick={() => {
              navigationMethod.current = "button";
              scrollToSlide(currentSlide + 1);
            }}
            aria-label="Next slide"
          >
            <ChevronRight size={20} className="text-ink" />
          </button>
        )}

        {/* Slides */}
        <div
          className="flex flex-1 overflow-x-auto snap-x snap-mandatory scroll-smooth min-h-0 pt-[env(safe-area-inset-top)]"
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
          ref={slidesRef}
          onScroll={handleScroll}
        >
          {/* Slide 1: Welcome — transparent, hero image shows from root */}
          <div className="flex flex-[0_0_100%] w-full snap-center min-h-0 items-end justify-center pb-4 px-5 lg:px-8 text-center">
            {/* `slide1Title` has existed in both catalogues all along; the
                Russian string was hard-coded here, so mint24.app/en showed a
                Russian heading on the first slide. */}
            <h2 className="relative z-10 text-3xl lg:text-2xl xl:text-3xl font-semibold text-white light:text-zinc-900 leading-tight whitespace-pre-line">
              {t("slide1Title")}
            </h2>
          </div>

          {/* Slide 2: One text → 5 formats */}
          <div className="flex flex-[0_0_100%] w-full snap-center flex-col items-center justify-end px-6 lg:px-8 text-center min-h-0 overflow-hidden bg-surface">
            <div className="flex flex-1 flex-col items-center justify-center gap-5 max-w-[340px] lg:max-w-[480px] w-full">
              {/* Example text bubble */}
              <div className="w-full text-left">
                <p className="text-[13px] lg:text-sm font-semibold text-ink-mute mb-2 ml-[5px]">{t("slide2TextLabel")}</p>
                <div className="w-full rounded-2xl bg-surface-mute px-5 py-4">
                  <p className="text-[15px] lg:text-base leading-relaxed text-ink-dim">
                    {t("slide2ExampleText")}
                  </p>
                </div>
              </div>

              {/* Arrow down */}
              <svg width="24" height="32" viewBox="0 0 24 32" fill="none" className="text-ink-mute shrink-0">
                <path d="M12 2v24M6 22l6 7 6-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>

              {/* 5 formats — colored chip grid */}
              <div className="flex flex-wrap justify-center gap-2.5 lg:gap-3 w-full">
                {[
                  { icon: "carousel", labelKey: "formatCarousel", bg: "rgba(52,211,153,0.23)", color: "#6ee7b7" },
                  { icon: "reels", labelKey: "formatReels", bg: "rgba(168,85,247,0.23)", color: "#d8b4fe" },
                  { icon: "stories", labelKey: "formatStories", bg: "rgba(251,146,60,0.23)", color: "#fdba74" },
                  { icon: "threads", labelKey: "formatThreads", bg: "rgba(96,165,250,0.23)", color: "#93c5fd" },
                  { icon: "post", labelKey: "formatPost", bg: "rgba(244,63,94,0.23)", color: "#fda4af" },
                ].map((item, i) => (
                  <div
                    key={item.icon}
                    className="flex items-center gap-2.5 rounded-2xl border-2 px-4 py-2.5 lg:px-5 lg:py-3"
                    style={{
                      borderColor: item.color,
                      animation: `onb-step-slide-up 0.4s ease-out ${0.08 * i}s both`,
                    }}
                  >
                    <div style={{ color: item.color }}>
                      <FormatIcon type={item.icon} />
                    </div>
                    <span className="text-[15px] lg:text-base font-semibold text-ink whitespace-nowrap">{t(item.labelKey)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Title — same position as slide 1 */}
            <div className="px-1 mt-4 mb-1 shrink-0">
              <h2 className="text-2xl lg:text-3xl font-semibold text-ink leading-tight">
                {t("slide2Title")}
              </h2>
            </div>
          </div>

          {/* Slide 3: Voice/text + photos mockup */}
          <div className="flex flex-[0_0_100%] w-full snap-center flex-col items-center justify-end px-6 lg:px-8 text-center min-h-0 overflow-hidden bg-surface">
            <div className="flex flex-1 flex-col items-center justify-center max-w-[380px] lg:max-w-[500px] w-full">
              {/* Chat input mockup card */}
              <div className="w-full rounded-2xl bg-surface-mute overflow-hidden shadow-sm">
                {/* Photo thumbnails row */}
                <div className="px-4 pt-4 pb-2">
                  <div className="flex gap-2 overflow-hidden">
                    {MOCK_PHOTOS.map((photo, i) => (
                      <div key={i} className="relative shrink-0 w-[52px] h-[52px] lg:w-[64px] lg:h-[64px] rounded-lg overflow-hidden">
                        <img
                          src={`/onboarding/${photo}`}
                          alt=""
                          decoding="async"
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                        <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-surface-dim flex items-center justify-center">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <path d="M1.5 1.5l5 5M6.5 1.5l-5 5" stroke="var(--ink-mute)" strokeWidth="1.2" strokeLinecap="round" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Photo hint */}
                  <div className="flex items-center gap-1.5 mt-2.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-ink-faint shrink-0">
                      <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth="1.6" />
                      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                      <path d="M3 16l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    </svg>
                    <span className="text-[12px] lg:text-[13px] text-ink-faint">{t("slide3PhotoHint")}</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="mx-4 border-t border-line" />

                {/* Text content area */}
                <div className="px-4 py-3 text-left">
                  <p className="text-[14px] lg:text-[15px] leading-relaxed text-ink-dim line-clamp-5">
                    {t("slide2ExampleText")}
                  </p>
                </div>

                {/* Bottom bar with + and mic */}
                <div className="px-4 pb-3 flex items-center justify-between">
                  <span className="text-xl text-ink-faint">+</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-ink-mute">
                    <rect x="9" y="2" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M5 11a7 7 0 0014 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M12 18v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
              </div>

              {/* Annotation arrows + labels */}
              <div className="w-full flex justify-between mt-1 px-1">
                {/* Left: arrow + label */}
                <div className="flex flex-col items-start max-w-[42%]">
                  <svg width="40" height="60" viewBox="0 0 40 60" fill="none" className="text-ink ml-4 shrink-0">
                    <path d="M24 58C20 36 12 18 12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M7 10l5-9 4 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="text-left mt-1">
                    <p className="text-[16px] font-bold text-ink leading-tight">{t("slide3AddPhoto")}</p>
                    <p className="text-[14px] text-ink-mute italic">{t("slide3AddPhotoHint")}</p>
                  </div>
                </div>

                {/* Right: arrow + label */}
                <div className="flex flex-col items-end max-w-[50%]" style={{ marginRight: 14 }}>
                  <svg width="40" height="60" viewBox="0 0 40 60" fill="none" className="text-ink mr-2 shrink-0">
                    <path d="M16 58C20 36 28 18 28 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M23 10l5-9 4 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="text-right mt-1">
                    <p className="text-[16px] font-bold text-ink leading-tight whitespace-pre-line">{t("slide3Title")}</p>
                  </div>
                </div>
              </div>

            </div>

            {/* Title — same position as slide 1 & 2 */}
            <div className="px-1 mt-4 mb-1 shrink-0">
              <h2 className="text-2xl lg:text-3xl font-semibold text-ink leading-tight">
                {t("slide3HeadTitle")}
              </h2>
            </div>
          </div>

          {/* Video slides: Content type showcases */}
          {VIDEO_SLIDES.map((slide, idx) => (
            <div
              key={slide.video}
              className="flex flex-[0_0_100%] w-full snap-center flex-col items-center bg-surface min-h-0 overflow-hidden"
            >
              {/* Video/Image with vignette */}
              <div className="relative flex-1 w-full flex items-end justify-center overflow-hidden px-10 pb-2 lg:px-20 lg:pb-4">
                <img
                  src={`${art}/${slide.image}`}
                  alt=""
                  className="max-h-[89%] max-w-[91%] lg:max-h-[85%] lg:max-w-[85%] object-contain rounded-lg lg:rounded-xl"
                />
                {/* Vignette overlay — edge fade to surface, stronger at bottom */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `
                      linear-gradient(to bottom, var(--surface) 0%, transparent 6%),
                      linear-gradient(to top, var(--surface) 0%, var(--surface) 6%, transparent 18%),
                      linear-gradient(to right, var(--surface) 0%, transparent 4%),
                      linear-gradient(to left, var(--surface) 0%, transparent 4%)
                    `,
                  }}
                />
              </div>

              {/* Title */}
              <div className="px-6 pt-4 pb-1 text-center shrink-0">
                <h2 className="text-[22px] lg:text-[26px] font-semibold text-ink leading-tight">
                  {t(slide.titleKey)}
                </h2>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className={cn(
          "px-5 py-5 lg:px-8 lg:py-6 pb-[calc(20px+env(safe-area-inset-bottom))] lg:pb-6 flex flex-col items-center gap-5 shrink-0 relative z-10",
          currentSlide === 0 ? "bg-transparent" : "bg-surface"
        )}>
          {/* Button */}
          <button
            key={shakeKey}
            className={cn(
              "relative w-full max-w-[320px] lg:max-w-[360px] overflow-hidden rounded-xl bg-mint px-8 py-3.5 text-lg font-semibold text-zinc-950 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(52,211,153,0.3)] active:scale-[0.98] cursor-pointer",
              btnShake && "animate-btn-shake"
            )}
            onClick={handleNext}
          >
            <span className="pointer-events-none absolute inset-0 overflow-hidden">
              <span
                className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent"
                style={{ animation: "shimmer 3s ease-in-out infinite" }}
              />
            </span>
            <span className="relative">
              {isLastSlide
                ? t("start")
                : isVideoSlide
                  ? ("btnKey" in VIDEO_SLIDES[videoSlideIndex]
                      ? t(VIDEO_SLIDES[videoSlideIndex].btnKey as string)
                      : t("try"))
                  : currentSlide === INTRO_SLIDES - 1
                    ? t("createCarousel")
                    : t("tryIt")}
            </span>
          </button>

          {/* Privacy link — always rendered for consistent button position, invisible on non-last slides */}
          <p className={cn(
            "text-[11px] text-center leading-snug max-w-[320px]",
            isLastSlide ? "text-ink-faint" : "opacity-0 select-none pointer-events-none"
          )}>
            {t("privacyHint")}{" "}
            {isLastSlide ? (
              <Link
                href="/privacy"
                className="text-mint underline hover:text-mint-dark"
              >
                {t("privacyLink")}
              </Link>
            ) : (
              <span>{t("privacyLink")}</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function FormatIcon({ type }: { type: string }) {
  const cls = "text-ink-dim";
  switch (type) {
    case "reels":
      return <Clapperboard size={20} className={cls} />;
    case "carousel":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={cls}>
          <rect x="2" y="4" width="14" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M18 7h1a3 3 0 013 3v6a3 3 0 01-3 3h-1" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case "stories":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={cls}>
          <rect x="6" y="2" width="12" height="20" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="12" cy="18" r="1" fill="currentColor" />
        </svg>
      );
    case "threads":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={cls}>
          <path d="M12 2C6.48 2 2 6 2 10.5c0 2.5 1.2 4.7 3 6.3V22l4.5-2.5c.8.2 1.6.3 2.5.3 5.52 0 10-4 10-8.5S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M8 9.5h8M8 13h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "post":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={cls}>
          <rect x="4" y="3" width="16" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}
